import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock, FileCheck2, Users, TrendingUp, Award } from "lucide-react";
import { LxpLayout } from "@/components/lexpractiq/LxpLayout";
import { getFirm, listAssignmentSubmissions } from "@/lib/api/lxp-firms.functions";

export const Route = createFileRoute("/_authenticated/lexpractiq/firms/$firmId/dashboard")({
  head: () => ({ meta: [{ title: "Firm dashboard | SA LexPractiq" }] }),
  component: FirmDashboard,
});

function hours(a: string | null, b: string | null): number | null {
  if (!a || !b) return null;
  return Math.max(0, (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000);
}

function FirmDashboard() {
  const { firmId } = Route.useParams();
  const getFn = useServerFn(getFirm);
  const subsFn = useServerFn(listAssignmentSubmissions);

  const firmQ = useQuery({ queryKey: ["firm", firmId], queryFn: () => getFn({ data: { firmId } }) });
  const assignments = firmQ.data?.assignments ?? [];

  const subsQ = useQuery({
    queryKey: ["firm-all-subs", firmId, assignments.map((a: any) => a.id).join(",")],
    enabled: assignments.length > 0,
    queryFn: async () => {
      const all = await Promise.all(
        assignments.map((a: any) => subsFn({ data: { assignmentId: a.id } })
          .then((r) => r.submissions.map((s: any) => ({ ...s, _assignment: a }))))
      );
      return all.flat();
    },
  });

  const subs = subsQ.data ?? [];
  const totalOpen = assignments.filter((a: any) => a.status === "open").length;
  const submittedToday = subs.filter((s: any) => {
    const d = new Date(s.created_at); const now = new Date();
    return d.toDateString() === now.toDateString();
  }).length;
  const pendingReview = subs.filter((s: any) => s.status !== "reviewed").length;
  const turnarounds = subs
    .map((s: any) => hours(s.created_at, s.reviewed_at))
    .filter((n: number | null): n is number => n !== null);
  const avgTurnaround = turnarounds.length
    ? Math.round(turnarounds.reduce((a, b) => a + b, 0) / turnarounds.length)
    : null;

  const leaderboard: Array<{ user_id: string; scoreAvg: number; count: number }> = [];
  const byUser = new Map<string, { total: number; count: number }>();
  for (const s of subs) {
    if (s.reviewer_score == null || !s.user_id) continue;
    const cur = byUser.get(s.user_id) ?? { total: 0, count: 0 };
    cur.total += s.reviewer_score; cur.count += 1;
    byUser.set(s.user_id, cur);
  }
  for (const [uid, v] of byUser) leaderboard.push({ user_id: uid, scoreAvg: Math.round(v.total / v.count), count: v.count });
  leaderboard.sort((a, b) => b.scoreAvg - a.scoreAvg);

  return (
    <LxpLayout>
      <div className="lxp-container" style={{ padding: "2rem 1.25rem" }}>
        <Link to="/lexpractiq/firms/$firmId" params={{ firmId }} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-navy">
          <ArrowLeft className="h-4 w-4" /> Back to workspace
        </Link>
        <h1 className="mt-4 font-display text-3xl text-navy">Firm dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Real-time view of your firm's assignments, submissions, and candidate performance.</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard icon={<FileCheck2 className="h-5 w-5" />} label="Open assignments" value={String(totalOpen)} />
          <MetricCard icon={<Users className="h-5 w-5" />} label="Submitted today" value={String(submittedToday)} />
          <MetricCard icon={<Clock className="h-5 w-5" />} label="Avg turnaround" value={avgTurnaround != null ? `${avgTurnaround}h` : "—"} />
          <MetricCard icon={<TrendingUp className="h-5 w-5" />} label="Pending review" value={String(pendingReview)} accent />
        </div>

        <section className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-input bg-card p-5">
            <h2 className="flex items-center gap-2 font-display text-lg text-navy"><Award className="h-4 w-4 text-gold" /> Top candidates</h2>
            {leaderboard.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">No scored submissions yet.</p>
            ) : (
              <ol className="mt-3 space-y-2">
                {leaderboard.slice(0, 10).map((row, i) => (
                  <li key={row.user_id} className="flex items-center justify-between rounded-md border border-input px-3 py-2">
                    <span className="text-sm"><span className="mr-2 font-mono text-xs text-muted-foreground">#{i + 1}</span>{row.user_id.slice(0, 8)}…</span>
                    <span className="text-sm font-semibold text-emerald">{row.scoreAvg}% · {row.count} sub{row.count === 1 ? "" : "s"}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <div className="rounded-xl border border-input bg-card p-5">
            <h2 className="font-display text-lg text-navy">Recent submissions</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="py-2">Assignment</th><th>Status</th><th>Score</th><th>Turnaround</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.slice(0, 12).map((s: any) => {
                    const t = hours(s.created_at, s.reviewed_at);
                    return (
                      <tr key={s.id} className="border-t border-input">
                        <td className="py-2 pr-3">{s._assignment.title}</td>
                        <td><StatusPill status={s.status} /></td>
                        <td>{s.reviewer_score != null ? `${s.reviewer_score}%` : "—"}</td>
                        <td>{t != null ? `${Math.round(t)}h` : "—"}</td>
                      </tr>
                    );
                  })}
                  {subs.length === 0 && <tr><td className="py-3 text-muted-foreground" colSpan={4}>No submissions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </LxpLayout>
  );
}

function MetricCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-gold bg-accent" : "border-input bg-card"}`}>
      <div className="flex items-center gap-2 text-muted-foreground">{icon}<span className="text-xs uppercase tracking-wider">{label}</span></div>
      <p className="mt-2 font-display text-3xl text-navy">{value}</p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    submitted: "bg-amber-100 text-amber-800",
    reviewed: "bg-emerald/15 text-emerald",
    draft: "bg-muted text-muted-foreground",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${map[status] ?? map.draft}`}>{status}</span>;
}
